import { useEffect, useState } from "react";

import { z } from "zod";

import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";

const APP_DOWNLOAD_HOST = import.meta.env.VITE_APP_DOWNLOAD_HOST;

const ManifestSchema = z.object({
  applicationId: z.string(),
  version: z.string(),
  resourcesURL: z.string(),
  data: z.object({
    mac_silicon: z.string(),
    mac_intel: z.string(),
    mac_universal: z.string(),
    win: z.string(),
  }),
});

export type Manifest = z.infer<typeof ManifestSchema>;

async function getManifest(): Promise<Manifest> {
  const res = await fetch(
    `https://${APP_DOWNLOAD_HOST}/app/v1/updates/manifest.json`,
  );
  if (!res.ok) {
    throw new Error("Failed to fetch manifest");
  }
  const json = await res.json();
  console.log("Manifest response", json);
  return ManifestSchema.parse(json);
}

export const DownloadAppUIWrapper = () => {
  return (
    <ScrollArea className="h-full p-4">
      <DownloadAppUI />
    </ScrollArea>
  );
};

export const DownloadAppUI = () => {
  const [manifest, setManifest] = useState<Manifest | null>(null);

  useEffect(() => {
    getManifest().then(setManifest);
  }, []);

  useEffect(() => {
    if (manifest) {
      console.log("Manifest updated:", manifest);
    }
  }, [manifest]);

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex-grow overflow-hidden">
        <div className="flex h-full flex-col space-y-8">
          <div className="flex flex-col space-y-4">
            <h2 className="text-lg font-medium">Download the Desktop App</h2>
            <p className="text-sm text-gray-400">
              For the best experience with Frontier X, download and run the
              desktop app on your Mac or Windows PC.
            </p>
          </div>
          <div className="flex flex-col space-y-4">
            <h3 className="text-md font-medium">
              Mac M series (Apple Silicon)
            </h3>
            <p className="text-sm text-gray-400">
              If you have a recent Mac with an M1, M2, or M3 processor use this
              version
            </p>
            <a href={manifest?.data.mac_silicon} download>
              <Button variant="default">Download Apple Silicon App</Button>
            </a>
          </div>
          <div className="flex flex-col space-y-4">
            <h3 className="text-md font-medium">Older Mac (Intel)</h3>
            <p className="text-sm text-gray-400">
              If you have an older mac before 2020, use this version.
            </p>
            <a href={manifest?.data.mac_intel} download>
              <Button variant="default">Download Intel App</Button>
            </a>
          </div>
          <div className="flex flex-col space-y-4">
            <h3 className="text-md font-medium">Mac Universal</h3>
            <p className="text-sm text-gray-400">
              Works on all Macs but has a larger file size
            </p>
            <a href={manifest?.data.mac_universal} download>
              <Button variant="default">Download Universal App</Button>
            </a>
          </div>
          <div className="flex flex-col space-y-4">
            <h3 className="text-md font-medium">Windows</h3>
            <p className="text-sm text-gray-400">
              For PCs running Windows 10 and 11
            </p>
            <a href={manifest?.data.win} download>
              <Button variant="default">Download Windows App</Button>
            </a>
          </div>
          <div>
            <p className="text-sm text-gray-400">
              Not sure which Mac ersion is best? Go to the Apple menu at the top
              left of your screen and select About This Mac. Look for the
              Processor line and it will tell you if you have an M series or
              Intel processor.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
